import { useCallback, useMemo } from 'react'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  cartAppliedCouponsSelector,
  couponCodeSchema,
  resolveCatchMessage,
  TCatchMessage,
  TCouponCodeSchema,
  useLoadingContext,
  useToastContext,
} from '@segway/core'
import { useAppSelector } from '@segway/core/src/store/hooks'
import { useForm } from 'react-hook-form'

import useCouponCode from '../couponCode/useCouponCode'

/**
 * 结算页优惠码 Hooks
 */
const useCheckoutCoupon = () => {
  const form = useForm<TCouponCodeSchema>({
    resolver: zodResolver(couponCodeSchema),
    defaultValues: {
      coupon_code: '',
    },
  })
  const toast = useToastContext()

  const loading = useLoadingContext()
  const cartAppliedCoupons = useAppSelector(cartAppliedCouponsSelector)
  const { applyCouponToCart, removeCouponFromCart } = useCouponCode()

  const isApply = useMemo(() => {
    return cartAppliedCoupons && cartAppliedCoupons.length > 0
  }, [cartAppliedCoupons])

  /**
   * 应用优惠券
   */
  const handleApply = useCallback(
    async (values: TCouponCodeSchema) => {
      try {
        if (values?.coupon_code === '' || values?.coupon_code === undefined) {
          return
        }
        loading.show()
        await applyCouponToCart(values.coupon_code)
      } catch (e) {
        toast.show({
          icon: 'fail',
          content: resolveCatchMessage(e as TCatchMessage),
        })
      } finally {
        loading.hide()
      }
    },
    [applyCouponToCart, loading, toast],
  )

  /**
   * 取消优惠券
   */
  const handleCancel = useCallback(async () => {
    try {
      loading.show()
      await removeCouponFromCart()
    } catch (e) {
      toast.show({
        icon: 'fail',
        content: resolveCatchMessage(e as TCatchMessage),
      })
    } finally {
      loading.hide()
    }
  }, [loading, removeCouponFromCart, toast])

  return { form, handleApply, handleCancel, isApply, cartAppliedCoupons }
}

export default useCheckoutCoupon
